/**
 * NxtKonekt – Admin Operations Dashboard
 * ────────────────────────────────────────
 * AdminBillReview.tsx — expanded with full ops visibility
 *
 * Tabs:
 *   1. Overview      — KPIs, MRR, churn, activity pulse
 *   2. Bill Analysis — extraction queue, corrections
 *   3. Users         — subscriber list, access management
 *   4. Trends        — carrier mix, line type patterns, confidence over time
 */

import { useState, useEffect } from "react";
import {
  collection, query, orderBy, where,
  getDocs, doc, updateDoc, serverTimestamp, limit,
  startAfter, DocumentSnapshot,
} from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { PDFDocument } from "pdf-lib";
import { db, auth } from "../firebaseConfig";
import { MONTHLY_PRICE } from "../constants/pricing";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LineTypeCounts {
  voice: number; tablet: number; hotspot: number;
  hsi: number; watch: number; other: number;
}

interface BillAnalysis {
  id: string; carrier: string; accountName: string;
  billingPeriod: { start_date: string; end_date: string };
  totalLines: number; lineTypeCounts: LineTypeCounts;
  totalAmountDue: number; confidenceScore: number;
  discrepancyFlag: boolean; lineCountVerified: boolean;
  needsReview: boolean; extractedAt: any; correctedAt?: any;
  correctedBy?: string; correctionNotes?: string;
  fullExtraction: any; corrected?: boolean;
  correctedCounts?: LineTypeCounts; correctedRates?: Record<string, number>;
  uid?: string;
}

interface AppUser {
  id: string; email: string; subscriptionStatus: string;
  plan: string; currentPeriodEnd: any; stripeCustomerId: string;
  createdAt?: any;
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AdminBillReview() {

  const [activeTab,  setActiveTab]  = useState<"overview"|"bills"|"users"|"trends">("overview");
  const [analyses,   setAnalyses]   = useState<BillAnalysis[]>([]);
  const [users,      setUsers]      = useState<AppUser[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState<"all"|"review"|"corrected">("all");
  const [userFilter, setUserFilter] = useState<"all"|"active"|"cancelled"|"past_due">("all");
  const [selected,   setSelected]   = useState<BillAnalysis | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [correction, setCorrection] = useState<Partial<BillAnalysis>>({});
  const [saving,     setSaving]     = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [lastDoc,    setLastDoc]    = useState<DocumentSnapshot | null>(null);
  const [hasMore,    setHasMore]    = useState(true);
  const [resetMsg,   setResetMsg]   = useState<string | null>(null);

  // Upload state
  const [uploading,     setUploading]     = useState(false);
  const [selectedFile,  setSelectedFile]  = useState<File | null>(null);
  const [uploadCarrier, setUploadCarrier] = useState("");
  const [uploadStatus,  setUploadStatus]  = useState<string | null>(null);

  const PAGE_SIZE = 25;

  const loadAnalyses = async (loadMore = false) => {
    setLoading(true);
    try {
      let q = query(collection(db, "bill_analyses"), orderBy("extractedAt", "desc"), limit(PAGE_SIZE));
      if (filter === "review")    q = query(collection(db, "bill_analyses"), where("needsReview","==",true),  orderBy("extractedAt","desc"), limit(PAGE_SIZE));
      if (filter === "corrected") q = query(collection(db, "bill_analyses"), where("corrected","==",true),    orderBy("correctedAt","desc"), limit(PAGE_SIZE));
      if (loadMore && lastDoc)    q = query(q, startAfter(lastDoc));
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as BillAnalysis));
      setAnalyses(loadMore ? prev => [...prev, ...docs] : docs);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      let q = query(collection(db, "users"), orderBy("email"), limit(100));
      if (userFilter !== "all") q = query(collection(db, "users"), where("subscriptionStatus","==",userFilter), limit(100));
      const snap = await getDocs(q);
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() } as AppUser)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (activeTab !== "users") loadAnalyses();
    if (activeTab === "users" || activeTab === "overview") loadUsers();
  }, [activeTab, filter, userFilter]);

  // ── Computed ──────────────────────────────────────────────────────────────
  const activeUsers      = users.filter(u => u.subscriptionStatus === "active" || u.subscriptionStatus === "cancelling");
  const cancelledUsers   = users.filter(u => u.subscriptionStatus === "cancelled");
  const pastDueUsers     = users.filter(u => u.subscriptionStatus === "past_due");
  const mrr              = activeUsers.length * MONTHLY_PRICE;
  const needsReviewCount = analyses.filter(a => a.needsReview).length;
  const correctedCount   = analyses.filter(a => a.corrected).length;
  const avgConfidence    = analyses.length ? analyses.reduce((s,a) => s+(a.confidenceScore||0),0)/analyses.length : 0;
  const avgAccountSize   = analyses.length ? analyses.reduce((s,a) => s+(a.totalLines||0),0)/analyses.length : 0;

  const carrierCounts = analyses.reduce((acc,a) => { acc[a.carrier]=(acc[a.carrier]||0)+1; return acc; }, {} as Record<string,number>);
  const lineTotals    = analyses.reduce((acc,a) => {
    const c = a.correctedCounts || a.lineTypeCounts || {};
    Object.entries(c).forEach(([k,v]) => { acc[k]=(acc[k]||0)+(v as number); });
    return acc;
  }, {} as Record<string,number>);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePasswordReset = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMsg(`Reset email sent to ${email}`);
      setTimeout(() => setResetMsg(null), 4000);
    } catch (err: any) { setResetMsg(`Error: ${err.message}`); }
  };

  const handleRevokeAccess = async (userId: string, email: string) => {
    if (!confirm(`Revoke access for ${email}?`)) return;
    try {
      await updateDoc(doc(db,"users",userId), { subscriptionStatus:"cancelled", plan:null, updatedAt:serverTimestamp() });
      setUsers(prev => prev.map(u => u.id===userId ? {...u,subscriptionStatus:"cancelled"} : u));
    } catch (err) { console.error(err); }
  };

  const openCorrection = (analysis: BillAnalysis) => {
    setSelected(analysis);
    setCorrection({ correctedCounts: analysis.correctedCounts||{...analysis.lineTypeCounts}, correctedRates: analysis.correctedRates||{}, correctionNotes: analysis.correctionNotes||"" });
    setCorrecting(true);
  };

  const saveCorrection = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const user = auth.currentUser;
      await updateDoc(doc(db,"bill_analyses",selected.id), {
        corrected:true, correctedCounts:correction.correctedCounts, correctedRates:correction.correctedRates,
        correctionNotes:correction.correctionNotes, correctedAt:serverTimestamp(), correctedBy:user?.email||"admin", needsReview:false
      });
      setAnalyses(prev => prev.map(a => a.id===selected.id ? {...a,corrected:true,needsReview:false,...correction} : a));
      setCorrecting(false); setSelected(null);
    } catch (err) { console.error(err); alert("Failed to save."); }
    finally { setSaving(false); }
  };

  const handleAnalyzeBill = async () => {
    if (!selectedFile || !uploadCarrier) {
      alert("Please select both a file and a carrier.");
      return;
    }

    setUploading(true);
    setUploadStatus("Reading file...");

    try {
      const MAX_SIZE = 15 * 1024 * 1024;
      let fileData = "";
      let isActuallySliced = selectedFile.size > MAX_SIZE;
      const mimeType = selectedFile.type;

      if (mimeType === "application/pdf") {
        setUploadStatus("Processing PDF...");
        const arrayBuffer = await selectedFile.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pageCount = pdfDoc.getPageCount();

        if (pageCount > 50) {
          isActuallySliced = true;
          setUploadStatus("Extracting first 50 pages...");
          alert('This bill is too large to analyze in full. We will extract a summary from the first 50 pages of the document.');
          const newPdf = await PDFDocument.create();
          const pages = await newPdf.copyPages(pdfDoc, Array.from({ length: 50 }, (_, i) => i));
          pages.forEach(p => newPdf.addPage(p));
          const pdfBytes = await newPdf.save();
          
          const base64 = btoa(
            pdfBytes.reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          fileData = base64;
        } else {
          let fileToProcess: Blob = selectedFile;
          if (selectedFile.size > MAX_SIZE) {
            setUploadStatus("Bill too large, slicing...");
            alert('This bill is too large to analyze in full. We will extract a summary from the first section of the document.');
            fileToProcess = selectedFile.slice(0, MAX_SIZE);
          }
          const base64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(fileToProcess);
            reader.onload = () => resolve(reader.result as string);
          });
          fileData = base64.split(",")[1];
        }
      } else {
        let fileToProcess: Blob = selectedFile;
        if (selectedFile.size > MAX_SIZE) {
          setUploadStatus("File too large, slicing...");
          fileToProcess = selectedFile.slice(0, MAX_SIZE);
        }
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(fileToProcess);
          reader.onload = () => resolve(reader.result as string);
        });
        fileData = base64.split(",")[1];
      }

      setUploadStatus("Analyzing bill (20-40s)...");

      const resp = await fetch("https://analyzebill-ibh2b2zgea-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileData, mimeType, carrier: uploadCarrier, isSliced: isActuallySliced })
      });

      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || "Analysis failed");
      }

      setUploadStatus("Success! Refreshing...");
      setTimeout(() => setUploadStatus(null), 3000);
      setSelectedFile(null);
      setUploadCarrier("");
      
      // Refresh table
      loadAnalyses();
    } catch (err: any) {
      console.error(err);
      alert(`Analysis failed: ${err.message}`);
      setUploadStatus(null);
    } finally {
      setUploading(false);
    }
  };

  // ── Formatters ────────────────────────────────────────────────────────────
  const fmt        = (n: number) => `$${(n||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const fmtK       = (n: number) => n>=1000 ? `$${(n/1000).toFixed(1)}k` : fmt(n);
  const fmtDate    = (ts: any)   => ts?.toDate?.()?.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})||"—";
  const scoreColor = (s: number) => s>=0.85?"#00c48c":s>=0.70?"#f5a623":"#ff4757";
  const statusColor= (s: string) => ({"active":"#00c48c","cancelling":"#00d4ff","past_due":"#f5a623","cancelled":"#ff4757"}[s])||"#7b87a1";
  const LINE_TYPES = ["voice","tablet","hotspot","hsi","watch","other"] as const;
  const filteredUsers = users.filter(u => !userSearch || u.email?.toLowerCase().includes(userSearch.toLowerCase()));

  const lineColors: Record<string,string> = { voice:"#00d4ff",tablet:"#00c48c",hotspot:"#f5a623",hsi:"#a78bfa",watch:"#f472b6",other:"#7b87a1" };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.root}>

      {/* TOP NAV */}
      <div style={S.topNav}>
        <div>
          <div style={S.navTitle}>NxtKonekt <span style={{color:"#00d4ff"}}>Admin</span></div>
          <div style={S.navSub}>Operations & Quality Control Dashboard</div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {resetMsg && <div style={S.toast}>{resetMsg}</div>}
          <button style={S.btnRefresh} onClick={() => activeTab==="users" ? loadUsers() : loadAnalyses()}>↻ Refresh</button>
        </div>
      </div>

      {/* TAB BAR */}
      <div style={S.tabBar}>
        {([
          {key:"overview",label:"Overview"},
          {key:"bills",   label:`Bill Analysis${needsReviewCount > 0 ? ` ⚠ ${needsReviewCount}` : ""}`},
          {key:"users",   label:`Users${pastDueUsers.length > 0 ? ` ⚠ ${pastDueUsers.length}` : ""}`},
          {key:"trends",  label:"Trends"},
        ] as const).map(t => (
          <button key={t.key} style={{...S.tab,...(activeTab===t.key?S.tabActive:{})}} onClick={()=>setActiveTab(t.key as any)}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{padding:"24px 32px 0"}}>

      {/* ══ OVERVIEW ══════════════════════════════════════════════════════ */}
      {activeTab==="overview" && (
        <div>
          <div style={S.kpiGrid}>
            {[
              {label:"Monthly Recurring Revenue",value:fmtK(mrr),           accent:"#00c48c",sub:`${activeUsers.length} active subscribers × $${MONTHLY_PRICE}`},
              {label:"Active Subscribers",        value:activeUsers.length,  accent:"#00d4ff",sub:`${cancelledUsers.length} cancelled · ${pastDueUsers.length} past due`},
              {label:"Bills Analyzed",            value:analyses.length,     accent:"#00d4ff",sub:`${correctedCount} corrected · ${needsReviewCount} needs review`},
              {label:"Avg Confidence",            value:`${(avgConfidence*100).toFixed(0)}%`,accent:scoreColor(avgConfidence),sub:"Gemini extraction accuracy"},
              {label:"Avg Account Size",          value:`${avgAccountSize.toFixed(0)} lines`,accent:"#f5a623",sub:"Lines per bill analyzed"},
              {label:"Past Due",                  value:pastDueUsers.length, accent:pastDueUsers.length>0?"#ff4757":"#00c48c",sub:"Payment failed — needs follow-up"},
            ].map(k=>(
              <div key={k.label} style={{...S.kpiCard,borderTopColor:k.accent}}>
                <div style={S.kpiLabel}>{k.label}</div>
                <div style={{...S.kpiValue,color:k.accent}}>{k.value}</div>
                <div style={S.kpiSub}>{k.sub}</div>
              </div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:24}}>
            <div style={S.panel}>
              <div style={S.panelTitle}>Carrier Breakdown</div>
              {Object.entries(carrierCounts).sort((a,b)=>b[1]-a[1]).map(([carrier,count])=>(
                <div key={carrier} style={S.barRow}>
                  <div style={S.barLabel}>{carrier}</div>
                  <div style={S.barTrack}><div style={{...S.barFill,width:`${(count/analyses.length)*100}%`,background:"#00d4ff"}}/></div>
                  <div style={S.barCount}>{count}</div>
                </div>
              ))}
              {!Object.keys(carrierCounts).length && <div style={S.empty}>No data yet</div>}
            </div>
            <div style={S.panel}>
              <div style={S.panelTitle}>Subscription Status</div>
              {[
                {label:"Active",    count:activeUsers.filter(u=>u.subscriptionStatus==="active").length,    color:"#00c48c"},
                {label:"Cancelling",count:activeUsers.filter(u=>u.subscriptionStatus==="cancelling").length,color:"#00d4ff"},
                {label:"Past Due",  count:pastDueUsers.length,  color:"#f5a623"},
                {label:"Cancelled", count:cancelledUsers.length,color:"#ff4757"},
              ].map(s=>(
                <div key={s.label} style={S.barRow}>
                  <div style={S.barLabel}>{s.label}</div>
                  <div style={S.barTrack}><div style={{...S.barFill,width:users.length?`${(s.count/users.length)*100}%`:"0%",background:s.color}}/></div>
                  <div style={S.barCount}>{s.count}</div>
                </div>
              ))}
            </div>
          </div>

          {pastDueUsers.length>0&&(
            <div style={{...S.panel,borderLeft:"3px solid #ff4757",marginBottom:24}}>
              <div style={S.panelTitle}>⚠ Past Due — Needs Follow-Up</div>
              {pastDueUsers.map(u=>(
                <div key={u.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #f0f2f8"}}>
                  <div style={{fontSize:"0.85rem",color:"#1c2540"}}>{u.email}</div>
                  <button style={S.btnSmall} onClick={()=>handlePasswordReset(u.email)}>Send Reset</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ BILL ANALYSIS ════════════════════════════════════════════════ */}
      {activeTab==="bills" && (
        <div>
          {/* UPLOAD SECTION */}
          <div style={S.uploadPanel}>
            <div style={S.panelTitle}>Analyze New Bill</div>
            <div style={S.panelSub}>Upload a PDF or Image for AI extraction</div>
            <div style={{display:"flex",gap:12,alignItems:"flex-end",marginTop:10}}>
              <div style={{flex:1}}>
                <label style={S.corrLabel}>Select Carrier</label>
                <select style={S.corrInput} value={uploadCarrier} onChange={e=>setUploadCarrier(e.target.value)} disabled={uploading}>
                  <option value="">— Select Carrier —</option>
                  <option value="AT&T">AT&T</option>
                  <option value="T-Mobile">T-Mobile</option>
                  <option value="Verizon">Verizon</option>
                  <option value="US Cellular">US Cellular</option>
                  <option value="Spectrum Mobile">Spectrum Mobile</option>
                </select>
              </div>
              <div style={{flex:2}}>
                <label style={S.corrLabel}>Upload Bill (PDF, JPG, PNG)</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={S.corrInput} 
                  onChange={e=>setSelectedFile(e.target.files?.[0]||null)} disabled={uploading}/>
              </div>
              <button 
                style={{...S.btnSave, height:42, display:"flex", alignItems:"center", gap:8, opacity: (uploading || !selectedFile || !uploadCarrier) ? 0.6 : 1}} 
                onClick={handleAnalyzeBill}
                disabled={uploading || !selectedFile || !uploadCarrier}
              >
                {uploading ? "Analyzing..." : "Analyze Bill"}
              </button>
            </div>
            {uploadStatus && <div style={{marginTop:12, fontSize:"0.8rem", color:"#00d4ff", fontWeight:600}}>{uploadStatus}</div>}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
            {[
              {label:"Total Extractions",value:analyses.length,     accent:"#00d4ff"},
              {label:"Needs Review",      value:needsReviewCount,   accent:"#f5a623"},
              {label:"Corrected",         value:correctedCount,     accent:"#00c48c"},
              {label:"Avg Confidence",    value:`${(avgConfidence*100).toFixed(0)}%`,accent:scoreColor(avgConfidence)},
            ].map(s=>(
              <div key={s.label} style={{...S.kpiCard,borderTopColor:s.accent}}>
                <div style={S.kpiLabel}>{s.label}</div>
                <div style={{...S.kpiValue,color:s.accent}}>{s.value}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            {(["all","review","corrected"] as const).map(f=>(
              <button key={f} style={{...S.tabLight,...(filter===f?S.tabLightActive:{})}} onClick={()=>setFilter(f)}>
                {f==="all"?"All":f==="review"?`⚠ Needs Review (${needsReviewCount})`:"✓ Corrected"}
              </button>
            ))}
          </div>
          {loading?<div style={S.empty}>Loading...</div>:analyses.length===0?<div style={S.empty}>No extractions found.</div>:(
            <div style={{...S.tableWrap,margin:0}}>
              <table style={S.table}>
                <thead><tr>{["Carrier","Account","Date","Lines","Voice","Tablet","Hotspot","HSI","Watch","Total Due","Confidence","Status","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {analyses.map(a=>(
                    <tr key={a.id} style={S.tr}>
                      <td style={S.td}><span style={S.badge}>{a.carrier}</span></td>
                      <td style={{...S.td,maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.accountName||"—"}</td>
                      <td style={S.td}>{fmtDate(a.extractedAt)}</td>
                      <td style={{...S.td,fontWeight:700}}>{a.totalLines||"—"}</td>
                      {LINE_TYPES.filter(t=>t!=="other").map(t=>(
                        <td key={t} style={S.td}>
                          <span style={a.correctedCounts?{color:"#00d4ff",fontWeight:700}:{}}>{(a.correctedCounts||a.lineTypeCounts)?.[t]??"—"}</span>
                        </td>
                      ))}
                      <td style={{...S.td,fontWeight:600}}>{fmt(a.totalAmountDue)}</td>
                      <td style={S.td}><span style={{...S.badge,background:scoreColor(a.confidenceScore)+"22",color:scoreColor(a.confidenceScore),border:`1px solid ${scoreColor(a.confidenceScore)}44`}}>{a.confidenceScore?`${(a.confidenceScore*100).toFixed(0)}%`:"—"}</span></td>
                      <td style={S.td}>
                        {a.corrected?<span style={{color:"#00d4ff",fontSize:"0.75rem",fontWeight:600}}>✓ Corrected</span>
                          :a.needsReview?<span style={{color:"#f5a623",fontSize:"0.75rem",fontWeight:600}}>⚠ Review</span>
                          :<span style={{color:"#00c48c",fontSize:"0.75rem",fontWeight:600}}>✓ OK</span>}
                      </td>
                      <td style={S.td}>
                        <div style={{display:"flex",gap:6}}>
                          <button style={S.btnView} onClick={()=>setSelected(a)}>View</button>
                          <button style={S.btnCorrect} onClick={()=>openCorrection(a)}>Correct</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {hasMore&&!loading&&<button style={S.btnLoadMore} onClick={()=>loadAnalyses(true)}>Load More</button>}
        </div>
      )}

      {/* ══ USERS ════════════════════════════════════════════════════════ */}
      {activeTab==="users" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
            {[
              {label:"Total Users",  value:users.length,        accent:"#00d4ff"},
              {label:"Active Paid",  value:activeUsers.length,  accent:"#00c48c"},
              {label:"Past Due",     value:pastDueUsers.length, accent:"#f5a623"},
              {label:"MRR",          value:fmtK(mrr),           accent:"#00c48c"},
            ].map(s=>(
              <div key={s.label} style={{...S.kpiCard,borderTopColor:s.accent}}>
                <div style={S.kpiLabel}>{s.label}</div>
                <div style={{...S.kpiValue,color:s.accent}}>{s.value}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:10,marginBottom:14,alignItems:"center"}}>
            <input style={S.searchInput} placeholder="Search by email..." value={userSearch} onChange={e=>setUserSearch(e.target.value)}/>
            {(["all","active","cancelled","past_due"] as const).map(f=>(
              <button key={f} style={{...S.tabLight,...(userFilter===f?S.tabLightActive:{})}} onClick={()=>setUserFilter(f)}>
                {f==="all"?"All":f==="active"?"Active":f==="cancelled"?"Cancelled":"Past Due"}
              </button>
            ))}
          </div>
          {loading?<div style={S.empty}>Loading users...</div>:(
            <div style={{...S.tableWrap,margin:0}}>
              <table style={S.table}>
                <thead><tr>{["Email","Status","Plan","Renews","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {filteredUsers.map(u=>(
                    <tr key={u.id} style={S.tr}>
                      <td style={S.td}>{u.email||"—"}</td>
                      <td style={S.td}><span style={{...S.badge,color:statusColor(u.subscriptionStatus),background:statusColor(u.subscriptionStatus)+"22",border:`1px solid ${statusColor(u.subscriptionStatus)}44`}}>{u.subscriptionStatus||"unknown"}</span></td>
                      <td style={S.td}>{u.plan||"—"}</td>
                      <td style={S.td}>{fmtDate(u.currentPeriodEnd)}</td>
                      <td style={S.td}>
                        <div style={{display:"flex",gap:6}}>
                          <button style={S.btnSmall} onClick={()=>handlePasswordReset(u.email)}>Reset PW</button>
                          {u.subscriptionStatus==="active"&&(
                            <button style={{...S.btnSmall,background:"#ff475722",color:"#ff4757",border:"1px solid #ff475744"}} onClick={()=>handleRevokeAccess(u.id,u.email)}>Revoke</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══ TRENDS ═══════════════════════════════════════════════════════ */}
      {activeTab==="trends" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:24}}>
          <div style={S.panel}>
            <div style={S.panelTitle}>Line Type Distribution</div>
            <div style={S.panelSub}>Across all analyzed accounts</div>
            {Object.entries(lineTotals).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).map(([type,count])=>{
              const total=Object.values(lineTotals).reduce((a,b)=>a+b,0);
              return(
                <div key={type} style={S.barRow}>
                  <div style={S.barLabel}>{type.toUpperCase()}</div>
                  <div style={S.barTrack}><div style={{...S.barFill,width:`${(count/total)*100}%`,background:lineColors[type]||"#00d4ff"}}/></div>
                  <div style={S.barCount}>{count} <span style={{color:"#7b87a1",fontSize:"0.7rem"}}>({((count/total)*100).toFixed(0)}%)</span></div>
                </div>
              );
            })}
            {!Object.keys(lineTotals).length&&<div style={S.empty}>No data yet</div>}
          </div>

          <div style={S.panel}>
            <div style={S.panelTitle}>Extraction Confidence</div>
            <div style={S.panelSub}>Gemini accuracy distribution</div>
            {[
              {label:"High (85%+)",    count:analyses.filter(a=>a.confidenceScore>=0.85).length, color:"#00c48c"},
              {label:"Medium (70-84%)",count:analyses.filter(a=>a.confidenceScore>=0.70&&a.confidenceScore<0.85).length,color:"#f5a623"},
              {label:"Low (<70%)",     count:analyses.filter(a=>a.confidenceScore<0.70).length,  color:"#ff4757"},
            ].map(b=>(
              <div key={b.label} style={S.barRow}>
                <div style={S.barLabel}>{b.label}</div>
                <div style={S.barTrack}><div style={{...S.barFill,width:analyses.length?`${(b.count/analyses.length)*100}%`:"0%",background:b.color}}/></div>
                <div style={S.barCount}>{b.count}</div>
              </div>
            ))}
          </div>

          <div style={S.panel}>
            <div style={S.panelTitle}>Correction Notes Log</div>
            <div style={S.panelSub}>What Gemini got wrong — training data</div>
            {analyses.filter(a=>a.correctionNotes).length===0
              ?<div style={S.empty}>No corrections recorded yet</div>
              :analyses.filter(a=>a.correctionNotes).map(a=>(
                <div key={a.id} style={{padding:"10px 0",borderBottom:"1px solid #f0f2f8"}}>
                  <div style={{fontSize:"0.72rem",color:"#7b87a1",marginBottom:3}}>{a.carrier} — {a.accountName} — {fmtDate(a.correctedAt)}</div>
                  <div style={{fontSize:"0.82rem",color:"#1c2540",lineHeight:1.5}}>{a.correctionNotes}</div>
                </div>
              ))
            }
          </div>

          <div style={S.panel}>
            <div style={S.panelTitle}>Account Size Segments</div>
            <div style={S.panelSub}>Lines per account analyzed</div>
            {[
              {label:"Small (1-25)",     count:analyses.filter(a=>a.totalLines<=25).length},
              {label:"Mid (26-75)",      count:analyses.filter(a=>a.totalLines>25&&a.totalLines<=75).length},
              {label:"Large (76-150)",   count:analyses.filter(a=>a.totalLines>75&&a.totalLines<=150).length},
              {label:"Enterprise (150+)",count:analyses.filter(a=>a.totalLines>150).length},
            ].map(b=>(
              <div key={b.label} style={S.barRow}>
                <div style={S.barLabel}>{b.label}</div>
                <div style={S.barTrack}><div style={{...S.barFill,width:analyses.length?`${(b.count/analyses.length)*100}%`:"0%",background:"#00d4ff"}}/></div>
                <div style={S.barCount}>{b.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      </div>

      {/* VIEW DRAWER */}
      {selected&&!correcting&&(
        <div style={S.overlay} onClick={()=>setSelected(null)}>
          <div style={S.drawer} onClick={e=>e.stopPropagation()}>
            <div style={S.drawerHeader}>
              <div>
                <div style={S.drawerTitle}>{selected.carrier} — {selected.accountName}</div>
                <div style={S.drawerSub}>Extracted {fmtDate(selected.extractedAt)}</div>
              </div>
              <button style={S.btnClose} onClick={()=>setSelected(null)}>✕</button>
            </div>
            <div style={S.drawerBody}>
              <div style={S.drawerSection}>
                <div style={S.drawerSectionTitle}>Validation</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap" as const,marginBottom:10}}>
                  <Flag ok={selected.lineCountVerified}       label="Line Count Verified"/>
                  <Flag ok={!selected.discrepancyFlag}        label="Financial Match"/>
                  <Flag ok={selected.confidenceScore>=0.80}   label={`Confidence ${(selected.confidenceScore*100).toFixed(0)}%`}/>
                </div>
                {selected.fullExtraction?.extraction_notes&&<div style={S.note}>{selected.fullExtraction.extraction_notes}</div>}
              </div>
              <div style={S.drawerSection}>
                <div style={S.drawerSectionTitle}>Line Type Counts</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                  {LINE_TYPES.map(t=>(
                    <div key={t} style={S.countCell}>
                      <div style={S.countType}>{t.toUpperCase()}</div>
                      <div style={S.countNum}>
                        {selected.correctedCounts?.[t]??selected.lineTypeCounts?.[t]??0}
                        {selected.correctedCounts&&selected.correctedCounts[t]!==selected.lineTypeCounts?.[t]&&(
                          <span style={{fontSize:"0.65rem",color:"#f5a623",marginLeft:4}}>was {selected.lineTypeCounts?.[t]}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={S.drawerSection}>
                <div style={S.drawerSectionTitle}>Full Extraction JSON</div>
                <pre style={S.jsonBlock}>{JSON.stringify(selected.fullExtraction,null,2)}</pre>
              </div>
              {selected.correctionNotes&&(
                <div style={S.drawerSection}>
                  <div style={S.drawerSectionTitle}>Correction Notes</div>
                  <div style={S.note}>{selected.correctionNotes}</div>
                </div>
              )}
            </div>
            <div style={S.drawerFooter}>
              <button style={S.btnCorrect} onClick={()=>openCorrection(selected)}>Open Correction Panel</button>
            </div>
          </div>
        </div>
      )}

      {/* CORRECTION PANEL */}
      {correcting&&selected&&(
        <div style={S.overlay}>
          <div style={{...S.drawer,width:560}}>
            <div style={S.drawerHeader}>
              <div>
                <div style={S.drawerTitle}>Correct Extraction</div>
                <div style={S.drawerSub}>{selected.carrier} — {selected.accountName}</div>
              </div>
              <button style={S.btnClose} onClick={()=>setCorrecting(false)}>✕</button>
            </div>
            <div style={S.drawerBody}>
              <div style={S.drawerSection}>
                <div style={S.drawerSectionTitle}>Line Type Counts</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                  {LINE_TYPES.map(t=>(
                    <div key={t} style={{display:"flex",flexDirection:"column" as const,gap:5}}>
                      <label style={S.corrLabel}>{t.charAt(0).toUpperCase()+t.slice(1)}<span style={{fontWeight:400,color:"#7b87a1",marginLeft:6}}>(Gemini: {selected.lineTypeCounts?.[t]??0})</span></label>
                      <input type="number" min={0} style={S.corrInput} value={correction.correctedCounts?.[t]??0}
                        onChange={e=>setCorrection(prev=>({...prev,correctedCounts:{...prev.correctedCounts as LineTypeCounts,[t]:parseInt(e.target.value)||0}}))}/>
                    </div>
                  ))}
                </div>
                {(()=>{
                  const counts=correction.correctedCounts as LineTypeCounts;
                  if(!counts) return null;
                  const sum=Object.values(counts).reduce((a,b)=>a+b,0);
                  const match=sum===selected.totalLines;
                  return <div style={{fontFamily:"monospace",fontSize:"0.8rem",fontWeight:600,color:match?"#00c48c":"#ff4757"}}>{match?"✓":"✗"} Count: {sum} / {selected.totalLines} total lines</div>;
                })()}
              </div>
              <div style={S.drawerSection}>
                <div style={S.drawerSectionTitle}>Rate Corrections (optional)</div>
                {["voice","tablet","hotspot","hsi","watch"].map(t=>(
                  <div key={t} style={{display:"flex",flexDirection:"column" as const,gap:5,marginBottom:10}}>
                    <label style={S.corrLabel}>Avg {t.charAt(0).toUpperCase()+t.slice(1)} Rate / Line</label>
                    <div style={{position:"relative" as const}}>
                      <span style={{position:"absolute" as const,left:11,top:"50%",transform:"translateY(-50%)",fontSize:"0.85rem",color:"#7b87a1"}}>$</span>
                      <input type="number" min={0} step={0.01} style={{...S.corrInput,paddingLeft:24}}
                        value={correction.correctedRates?.[t]??""} placeholder={`${selected.fullExtraction?.average_rate_by_type?.[t]||"0"}`}
                        onChange={e=>setCorrection(prev=>({...prev,correctedRates:{...prev.correctedRates,[t]:parseFloat(e.target.value)||0}}))}/>
                    </div>
                  </div>
                ))}
              </div>
              <div style={S.drawerSection}>
                <div style={S.drawerSectionTitle}>Correction Notes</div>
                <textarea style={S.notesInput} rows={4} placeholder="Describe what Gemini got wrong — this becomes training data..."
                  value={correction.correctionNotes||""} onChange={e=>setCorrection(prev=>({...prev,correctionNotes:e.target.value}))}/>
                <div style={{fontSize:"0.75rem",color:"#7b87a1",marginTop:6,lineHeight:1.5}}>Be specific — e.g. "Hotspot lines misclassified as tablet."</div>
              </div>
            </div>
            <div style={S.drawerFooter}>
              <button style={S.btnCancel} onClick={()=>setCorrecting(false)}>Cancel</button>
              <button style={{...S.btnSave,opacity:saving?0.6:1}} onClick={saveCorrection} disabled={saving}>{saving?"Saving...":"Save Correction"}</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function Flag({ok,label}:{ok:boolean;label:string}) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:6,fontSize:"0.8rem",fontWeight:600,
      color:ok?"#00c48c":"#ff4757",background:ok?"rgba(0,196,140,0.1)":"rgba(255,71,87,0.1)",
      border:`1px solid ${ok?"rgba(0,196,140,0.25)":"rgba(255,71,87,0.25)"}`,borderRadius:6,padding:"5px 10px"}}>
      {ok?"✓":"✗"} {label}
    </div>
  );
}

const S: Record<string,React.CSSProperties> = {
  root:        {fontFamily:"'DM Sans',sans-serif",background:"#f4f6fc",minHeight:"100vh",padding:"0 0 60px"},
  topNav:      {background:"#0a0f1e",padding:"16px 32px",display:"flex",justifyContent:"space-between",alignItems:"center"},
  navTitle:    {fontFamily:"'Syne',sans-serif",fontSize:"1.1rem",fontWeight:800,color:"#fff"},
  navSub:      {fontSize:"0.75rem",color:"#7b87a1",marginTop:2},
  tabBar:      {background:"#0a0f1e",padding:"0 32px",display:"flex",gap:4,borderBottom:"1px solid rgba(255,255,255,0.06)"},
  tab:         {fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:600,padding:"12px 20px",background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",borderBottom:"2px solid transparent"},
  tabActive:   {color:"#00d4ff",borderBottom:"2px solid #00d4ff"},
  tabLight:    {fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:600,padding:"8px 18px",borderRadius:8,border:"1.5px solid #dce2ef",background:"#fff",color:"#7b87a1",cursor:"pointer"},
  tabLightActive:{background:"#0a0f1e",color:"#fff",border:"1.5px solid #0a0f1e"},
  kpiGrid:     {display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20},
  kpiCard:     {background:"#fff",borderRadius:10,padding:"16px 20px",borderTop:"3px solid #dce2ef",border:"1px solid #dce2ef"},
  kpiLabel:    {fontSize:"0.68rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"1px",color:"#7b87a1",marginBottom:6},
  kpiValue:    {fontFamily:"'Syne',sans-serif",fontSize:"1.6rem",fontWeight:800,lineHeight:1},
  kpiSub:      {fontSize:"0.72rem",color:"#7b87a1",marginTop:4},
  panel:       {background:"#fff",borderRadius:10,padding:"20px 24px",border:"1px solid #dce2ef"},
  uploadPanel: {background:"#fff",borderRadius:10,padding:"20px 24px",border:"1.5px solid #00d4ff22",marginBottom:20,borderLeft:"4px solid #00d4ff"},
  panelTitle:  {fontFamily:"'Syne',sans-serif",fontSize:"0.95rem",fontWeight:700,color:"#0a0f1e",marginBottom:4},
  panelSub:    {fontSize:"0.75rem",color:"#7b87a1",marginBottom:16},
  barRow:      {display:"flex",alignItems:"center",gap:10,marginBottom:10},
  barLabel:    {width:140,fontSize:"0.78rem",fontWeight:600,color:"#3d4b6e",flexShrink:0},
  barTrack:    {flex:1,height:8,background:"#f0f2f8",borderRadius:4,overflow:"hidden"},
  barFill:     {height:"100%",borderRadius:4,transition:"width 0.3s"},
  barCount:    {width:60,fontSize:"0.78rem",fontWeight:700,color:"#0a0f1e",textAlign:"right"},
  tableWrap:   {background:"#fff",borderRadius:12,border:"1px solid #dce2ef",overflow:"auto"},
  table:       {width:"100%",borderCollapse:"collapse",fontSize:"0.83rem"},
  th:          {padding:"12px 14px",textAlign:"left",fontWeight:700,fontSize:"0.7rem",textTransform:"uppercase",letterSpacing:"0.8px",color:"#7b87a1",background:"#f4f6fc",borderBottom:"1px solid #dce2ef",whiteSpace:"nowrap"},
  tr:          {borderBottom:"1px solid #f0f2f8"},
  td:          {padding:"11px 14px",color:"#1c2540",whiteSpace:"nowrap"},
  badge:       {fontFamily:"'DM Mono',monospace",fontSize:"0.72rem",fontWeight:500,padding:"3px 8px",borderRadius:4,background:"#f0f2f8",color:"#3d4b6e"},
  btnRefresh:  {fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:600,padding:"8px 18px",borderRadius:8,border:"1px solid rgba(255,255,255,0.15)",background:"transparent",color:"#fff",cursor:"pointer"},
  btnView:     {fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",fontWeight:600,padding:"5px 12px",borderRadius:6,border:"1.5px solid #dce2ef",background:"#fff",color:"#3d4b6e",cursor:"pointer"},
  btnCorrect:  {fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",fontWeight:600,padding:"5px 12px",borderRadius:6,border:"none",background:"#f5a623",color:"#fff",cursor:"pointer"},
  btnSmall:    {fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:600,padding:"4px 10px",borderRadius:5,border:"1px solid #dce2ef",background:"#f4f6fc",color:"#3d4b6e",cursor:"pointer"},
  btnLoadMore: {display:"block",margin:"20px auto 0",fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",fontWeight:600,padding:"10px 28px",borderRadius:8,border:"1.5px solid #dce2ef",background:"#fff",color:"#3d4b6e",cursor:"pointer"},
  btnCancel:   {fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",fontWeight:600,padding:"10px 20px",borderRadius:8,border:"1.5px solid #dce2ef",background:"#fff",color:"#3d4b6e",cursor:"pointer"},
  btnSave:     {fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",fontWeight:700,padding:"10px 24px",borderRadius:8,border:"none",background:"#00c48c",color:"#fff",cursor:"pointer"},
  btnClose:    {background:"transparent",border:"none",color:"#7b87a1",fontSize:"1rem",cursor:"pointer",padding:4},
  searchInput: {fontFamily:"'DM Sans',sans-serif",fontSize:"0.88rem",padding:"8px 14px",borderRadius:8,border:"1.5px solid #dce2ef",background:"#fff",color:"#0a0f1e",width:260},
  overlay:     {position:"fixed",inset:0,background:"rgba(10,15,30,0.6)",zIndex:1000,display:"flex",justifyContent:"flex-end"},
  drawer:      {width:520,height:"100vh",background:"#fff",display:"flex",flexDirection:"column",overflow:"hidden"},
  drawerHeader:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"20px 24px",borderBottom:"1px solid #dce2ef",background:"#0a0f1e"},
  drawerTitle: {fontFamily:"'Syne',sans-serif",fontSize:"1rem",fontWeight:800,color:"#fff"},
  drawerSub:   {fontSize:"0.78rem",color:"#7b87a1",marginTop:3},
  drawerBody:  {flex:1,overflowY:"auto",padding:"20px 24px"},
  drawerFooter:{padding:"16px 24px",borderTop:"1px solid #dce2ef",display:"flex",justifyContent:"flex-end",gap:10},
  drawerSection:{marginBottom:24},
  drawerSectionTitle:{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"1px",color:"#7b87a1",marginBottom:12,paddingBottom:6,borderBottom:"1px solid #f0f2f8"},
  note:        {fontSize:"0.82rem",color:"#3d4b6e",background:"#f4f6fc",borderRadius:6,padding:"10px 12px",lineHeight:1.5},
  countCell:   {background:"#f4f6fc",borderRadius:8,padding:"10px 14px",textAlign:"center"},
  countType:   {fontFamily:"'DM Mono',monospace",fontSize:"0.65rem",fontWeight:600,color:"#7b87a1",letterSpacing:"1px",marginBottom:4},
  countNum:    {fontFamily:"'Syne',sans-serif",fontSize:"1.4rem",fontWeight:800,color:"#0a0f1e"},
  jsonBlock:   {fontSize:"0.72rem",background:"#0a0f1e",color:"#00d4ff",borderRadius:8,padding:14,overflowX:"auto",maxHeight:300,overflowY:"auto",lineHeight:1.5},
  corrLabel:   {fontSize:"0.72rem",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.6px",color:"#3d4b6e"},
  corrInput:   {fontFamily:"'DM Sans',sans-serif",fontSize:"0.95rem",fontWeight:600,padding:"9px 12px",borderRadius:7,border:"1.5px solid #dce2ef",background:"#f4f6fc",color:"#0a0f1e",width:"100%"},
  notesInput:  {fontFamily:"'DM Sans',sans-serif",fontSize:"0.88rem",padding:"10px 12px",borderRadius:7,border:"1.5px solid #dce2ef",background:"#f4f6fc",color:"#0a0f1e",width:"100%",resize:"vertical"},
  empty:       {padding:"40px",textAlign:"center",color:"#7b87a1"},
  toast:       {fontSize:"0.78rem",color:"#00c48c",background:"rgba(0,196,140,0.1)",border:"1px solid rgba(0,196,140,0.3)",borderRadius:6,padding:"6px 12px"},
};
